import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, map } from 'rxjs';
import { AppInitializationState, JwtPayload } from '../../domain/common.models';

/**
 * JWT Processing Service
 * Handles JWT token parsing and validation operations
 * Extended with route initialization functionality
 *
 * @description Clean architecture service layer for JWT operations
 */
@Injectable({ providedIn: 'root' })
export class JwtProcessingService {
    private readonly activatedRoute = inject(ActivatedRoute);

    /**
     * Initialize app state from route query parameters
     * Returns clean Observable that components can pipe with their own destroyRef
     *
     * @returns Observable of app state updates
     */
    initializeAppFromRoute(): Observable<AppInitializationState> {
        return this.activatedRoute.queryParams.pipe(
            map((params) => {
                const token = params['token'] || '';
                const lang = params['lang'] || 'id';
                const payload = this.parseJwtPayload(token);
                const phoneNumber = this.extractPhoneNumber(payload);

                return { token, payload, phoneNumber, lang };
            }),
        );
    }

    /**
     * Parses JWT token and extracts payload
     * @param token - JWT token string
     * @returns Parsed payload or null if invalid
     */
    parseJwtPayload(token: string): JwtPayload | null {
        if (!token) return null;

        try {
            const base64Url = token.split('.')[1];
            if (!base64Url) return null;

            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(window.atob(base64));

            // Validate required fields
            if (!payload.wallet_id || !payload.phone_number) {
                console.warn('JWT payload missing required fields:', payload);
                return null;
            }

            return payload;
        } catch (error) {
            console.error('Failed to parse JWT token:', error);
            return null;
        }
    }

    /**
     * Extracts company ID from JWT payload with fallback
     * @param payload - JWT payload
     * @returns Company ID or empty string
     */
    extractCompanyId(payload: JwtPayload | null): string {
        return payload?.client_ref_id || payload?.company_id || '';
    }

    /**
     * Extracts user ID from JWT payload with fallback
     * @param payload - JWT payload
     * @returns User ID or empty string
     */
    extractUserId(payload: JwtPayload | null): string {
        return payload?.user_ref_id || payload?.user_id || '';
    }

    /**
     * Checks if JWT token is expired
     * @param payload - JWT payload
     * @returns true if token is expired
     */
    isTokenExpired(payload: JwtPayload | null): boolean {
        if (!payload?.exp) return false;
        return payload.exp < Math.floor(Date.now() / 1000);
    }

    /**
     * Validates JWT payload structure
     * @param payload - JWT payload to validate
     * @returns true if payload is valid
     */
    isValidPayload(payload: JwtPayload | null): boolean {
        if (!payload) return false;

        const hasRequiredFields = !!(payload.wallet_id && payload.phone_number);
        const hasUserIdentifier = !!(this.extractUserId(payload) || this.extractCompanyId(payload));

        return hasRequiredFields && hasUserIdentifier;
    }

    /**
     * Extracts phone number with formatting
     * @param payload - JWT payload
     * @returns Formatted phone number
     */
    extractPhoneNumber(payload: JwtPayload | null): string {
        return payload?.phone_number || '';
    }
}
